import { getApiBaseUrl } from '../../lib/api';
/**
 * Pet Requests Management Page
 * 
 * Handles RRA 2025 pet request workflow:
 * - View pending pet requests
 * - Respond within 28-day SLA
 * - Track approvals/refusals
 */
import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { 
  PawPrint, Clock, CheckCircle, XCircle, AlertTriangle,
  ChevronRight
} from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent, CardDescription, CardFooter } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { 
  Dialog, DialogContent, DialogHeader, DialogTitle, 
  DialogDescription, DialogFooter 
} from '../../components/ui/dialog';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Checkbox } from '../../components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from 'sonner';

const API_URL = getApiBaseUrl();

const PetRequestsPage = () => {
  const [loading, setLoading] = useState(true);
  const [requests, setRequests] = useState([]);
  const [rules, setRules] = useState(null);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [showResponseDialog, setShowResponseDialog] = useState(false);
  const [responding, setResponding] = useState(false);
  const [responseForm, setResponseForm] = useState({
    approved: true,
    conditions: '',
    refusalReasons: [],
    refusalNotes: '',
    petInsuranceRequired: false,
  });

  const fetchData = useCallback(async () => {
    const token = localStorage.getItem('token');
    
    try {
      setLoading(true);
      
      // Fetch pet request rules
      const rulesRes = await fetch(`${API_URL}/api/rra-compliance/policy/pet-request-rules`);
      if (rulesRes.ok) {
        setRules(await rulesRes.json());
      }
      
      // TODO: Fetch pet requests list (requires new endpoint)
      // For now, show empty state
      setRequests([]);
      
    } catch (error) {
      console.error('Error fetching pet requests:', error);
      toast.error('Failed to load pet requests');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  const getDaysRemaining = (dueDate) => {
    if (!dueDate) return null;
    const due = new Date(dueDate);
    const today = new Date();
    const diff = Math.ceil((due - today) / (1000 * 60 * 60 * 24));
    return diff;
  };

  const getStatusBadge = (status, dueDate) => {
    const daysRemaining = getDaysRemaining(dueDate);
    
    const badges = {
      'pending': daysRemaining <= 3 
        ? { label: `Urgent (${daysRemaining}d)`, variant: 'destructive' }
        : daysRemaining <= 7 
          ? { label: `Due Soon (${daysRemaining}d)`, variant: 'warning' }
          : { label: 'Pending', variant: 'secondary' },
      'approved': { label: 'Approved', variant: 'default' },
      'approved_conditional': { label: 'Approved (Conditional)', variant: 'default' },
      'refused_valid': { label: 'Refused (Valid)', variant: 'outline' },
      'refused_invalid': { label: 'Refused (Review)', variant: 'destructive' },
      'expired_deemed_approved': { label: 'Deemed Approved', variant: 'default' },
    };
    const badge = badges[status] || { label: status, variant: 'outline' };
    return <Badge variant={badge.variant}>{badge.label}</Badge>;
  };

  const handleRespond = (request) => {
    setSelectedRequest(request);
    setResponseForm({
      approved: true,
      conditions: '',
      refusalReasons: [],
      refusalNotes: '',
      petInsuranceRequired: false,
    });
    setShowResponseDialog(true);
  };

  const handleSubmitResponse = async () => {
    if (!selectedRequest) return;
    
    const token = localStorage.getItem('token');
    
    try {
      setResponding(true);
      
      const response = await fetch(`${API_URL}/api/rra-compliance/pets/respond`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          request_id: selectedRequest.id,
          approved: responseForm.approved,
          conditions: responseForm.conditions || null,
          refusal_reasons: responseForm.refusalReasons,
          refusal_notes: responseForm.refusalNotes || null,
          pet_insurance_required: responseForm.petInsuranceRequired,
        }),
      });
      
      if (response.ok) {
        toast.success(responseForm.approved 
          ? 'Pet request has been approved' 
          : 'Pet request has been declined'
        );
        setShowResponseDialog(false);
        fetchData();
      } else {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to submit response');
      }
    } catch (error) {
      toast.error(error.message);
    } finally {
      setResponding(false);
    }
  };

  const validRefusalReasons = [
    { value: 'property_unsuitable', label: 'Property unsuitable (e.g., no garden)' },
    { value: 'superior_lease_prohibits', label: 'Superior lease prohibits pets' },
    { value: 'building_rules', label: 'Building management rules prohibit pets' },
    { value: 'insurance_restrictions', label: 'Insurance restrictions' },
    { value: 'size_inappropriate', label: 'Property size inappropriate for pet type' },
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="pet-requests-loading">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6" data-testid="pet-requests-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <PawPrint className="h-6 w-6 text-primary" />
            Pet Requests
          </h1>
          <p className="text-muted-foreground">
            Manage tenant pet requests under RRA 2025
          </p>
        </div>
        <Link to="/compliance">
          <Button variant="outline" data-testid="back-to-dashboard-btn">
            Back to Dashboard
          </Button>
        </Link>
      </div>

      {/* Rules Info */}
      {rules && (
        <Card className="bg-amber-50 border-amber-200 dark:bg-amber-950/20 dark:border-amber-900">
          <CardContent className="pt-4">
            <div className="flex items-start gap-3">
              <AlertTriangle className="h-5 w-5 text-amber-600 mt-0.5" />
              <div>
                <p className="font-medium text-amber-800 dark:text-amber-200">
                  28-Day Response Deadline
                </p>
                <p className="text-sm text-amber-700 dark:text-amber-300 mt-1">
                  Under the Renters&apos; Rights Act 2025, you must respond to pet requests within {rules.response_deadline_days} days.
                  {rules.deemed_consent_on_no_response && (
                    <strong> Failure to respond will result in automatic deemed consent.</strong>
                  )}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Requests List */}
      {requests.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <PawPrint className="h-12 w-12 mx-auto text-muted-foreground/50 mb-4" />
            <h3 className="text-lg font-medium">No Pet Requests</h3>
            <p className="text-muted-foreground mt-1">
              You don&apos;t have any pending pet requests at this time.
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {requests.map((request) => {
            const daysRemaining = getDaysRemaining(request.response_due_date);
            const isUrgent = daysRemaining !== null && daysRemaining <= 7;
            
            return (
              <Card 
                key={request.id} 
                className={isUrgent && request.status === 'pending' ? 'border-amber-300' : ''}
                data-testid={`pet-request-card-${request.id}`}
              >
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className={`p-2 rounded-full ${
                        request.pet_type === 'dog' ? 'bg-amber-100' :
                        request.pet_type === 'cat' ? 'bg-purple-100' :
                        'bg-blue-100'
                      }`}>
                        <PawPrint className="h-5 w-5" />
                      </div>
                      <div>
                        <CardTitle className="text-base">
                          {request.pet_type.charAt(0).toUpperCase() + request.pet_type.slice(1)}
                          {request.pet_breed && ` - ${request.pet_breed}`}
                        </CardTitle>
                        <CardDescription>
                          {request.pet_name && `${request.pet_name} • `}
                          Requested: {formatDate(request.request_date)}
                        </CardDescription>
                      </div>
                    </div>
                    {getStatusBadge(request.status, request.response_due_date)}
                  </div>
                </CardHeader>
                <CardContent>
                  {request.request_reason && (
                    <p className="text-sm text-muted-foreground mb-3">
                      <strong>Reason:</strong> {request.request_reason}
                    </p>
                  )}
                  
                  {request.status === 'pending' && (
                    <div className="flex items-center gap-2 text-sm">
                      <Clock className="h-4 w-4" />
                      <span>
                        Response due: {formatDate(request.response_due_date)}
                        {daysRemaining !== null && (
                          <span className={daysRemaining <= 3 ? 'text-red-600 font-medium' : ''}>
                            {' '}({daysRemaining} days remaining)
                          </span>
                        )}
                      </span>
                    </div>
                  )}
                </CardContent>
                {request.status === 'pending' && (
                  <CardFooter className="border-t pt-4">
                    <Button 
                      onClick={() => handleRespond(request)}
                      className="ml-auto"
                      data-testid={`respond-btn-${request.id}`}
                    >
                      Respond to Request
                      <ChevronRight className="h-4 w-4 ml-1" />
                    </Button>
                  </CardFooter>
                )}
              </Card>
            );
          })}
        </div>
      )}

      {/* Response Dialog */}
      <Dialog open={showResponseDialog} onOpenChange={setShowResponseDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Respond to Pet Request</DialogTitle>
            <DialogDescription>
              {selectedRequest && (
                <>
                  {selectedRequest.pet_type.charAt(0).toUpperCase() + selectedRequest.pet_type.slice(1)}
                  {selectedRequest.pet_breed && ` - ${selectedRequest.pet_breed}`}
                  {selectedRequest.pet_name && ` (${selectedRequest.pet_name})`}
                </>
              )}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* Decision */}
            <div className="space-y-2">
              <Label>Your Decision</Label>
              <div className="flex gap-4">
                <Button
                  type="button"
                  variant={responseForm.approved ? 'default' : 'outline'}
                  className="flex-1"
                  onClick={() => setResponseForm({ ...responseForm, approved: true })}
                  data-testid="approve-btn"
                >
                  <CheckCircle className="h-4 w-4 mr-2" />
                  Approve
                </Button>
                <Button
                  type="button"
                  variant={!responseForm.approved ? 'destructive' : 'outline'}
                  className="flex-1"
                  onClick={() => setResponseForm({ ...responseForm, approved: false })}
                  data-testid="refuse-btn"
                >
                  <XCircle className="h-4 w-4 mr-2" />
                  Refuse
                </Button>
              </div>
            </div>

            {/* Approval Conditions */}
            {responseForm.approved && (
              <>
                <div className="space-y-2">
                  <Label htmlFor="conditions">Conditions (optional)</Label>
                  <Textarea
                    id="conditions"
                    placeholder="e.g., Pet must be kept on a lead in communal areas..."
                    value={responseForm.conditions}
                    onChange={(e) => setResponseForm({ ...responseForm, conditions: e.target.value })}
                    data-testid="conditions-input"
                  />
                </div>
                
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="pet_insurance"
                    checked={responseForm.petInsuranceRequired}
                    onCheckedChange={(checked) => 
                      setResponseForm({ ...responseForm, petInsuranceRequired: checked })
                    }
                    data-testid="insurance-checkbox"
                  />
                  <Label htmlFor="pet_insurance">Require pet insurance</Label>
                </div>
              </>
            )}

            {/* Refusal Reasons */}
            {!responseForm.approved && (
              <>
                <div className="space-y-2">
                  <Label>Refusal Reason (required)</Label>
                  <p className="text-xs text-muted-foreground">
                    Under RRA 2025, refusals must be on valid grounds only.
                  </p>
                  <Select
                    value={responseForm.refusalReasons[0] || ''}
                    onValueChange={(value) => 
                      setResponseForm({ ...responseForm, refusalReasons: [value] })
                    }
                  >
                    <SelectTrigger data-testid="refusal-reason-select">
                      <SelectValue placeholder="Select a valid reason..." />
                    </SelectTrigger>
                    <SelectContent>
                      {validRefusalReasons.map((reason) => (
                        <SelectItem key={reason.value} value={reason.value}>
                          {reason.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="refusal_notes">Additional Notes</Label>
                  <Textarea
                    id="refusal_notes"
                    placeholder="Provide details to support your refusal..."
                    value={responseForm.refusalNotes}
                    onChange={(e) => setResponseForm({ ...responseForm, refusalNotes: e.target.value })}
                    data-testid="refusal-notes-input"
                  />
                </div>
              </>
            )}
          </div>

          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => setShowResponseDialog(false)}
              data-testid="cancel-response-btn"
            >
              Cancel
            </Button>
            <Button 
              onClick={handleSubmitResponse}
              disabled={responding || (!responseForm.approved && responseForm.refusalReasons.length === 0)}
              data-testid="submit-response-btn"
            >
              {responding ? 'Submitting...' : 'Submit Response'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PetRequestsPage;
